import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { createQuote } from "../../redux/Slices/quoteSlice";
import { escrowAPI } from "../../services/escrowAPI";
import { PurchaseQuoteTypeEnum } from "../../modules/shared/model/quote.enum";
import { checkMaxPurchaseQuoteLimit } from "../../utils/categoryFormSettingsUtils";
import { Button } from "./Button";
import { X, DollarSign, CreditCard, AlertCircle, Loader2, CheckCircle } from "lucide-react";
import CreditPaymentModal from "../payment/CreditPaymentModal";
import { useNavigate } from "react-router-dom";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

interface QuotePurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (quote: any) => void;
  quote: {
    id: string;
    amount: number;
    job?: {
      id: string;
      title?: string;
    };
    professional?: {
      id: string;
      firstName?: string;
      lastName?: string;
    };
    isEmergency?: boolean;
    useEmergencyCredits?: boolean;
    isPromotional?: boolean; // Flag for promotional (free) quotes
  } | null;
  userCredits?: number;
  refetchCredits?: () => void;
  useEmergencyCredits?: boolean;
  emergencyAvailableCredits?: number;
  emergencyUsedCredits?: number;
}

const QuotePurchaseModal: React.FC<QuotePurchaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  quote,
  userCredits = 0,
  refetchCredits,
  useEmergencyCredits = false,
  emergencyAvailableCredits = 0,
  emergencyUsedCredits = 0
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state: any) => state.auth);
  const { setting } = useAppSelector((state: any) => state.categoryFormSettings);
  const [paymentMethod, setPaymentMethod] = useState<"dollars" | "credits">("dollars");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCreditPaymentModal, setShowCreditPaymentModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [maxLimitExceeded, setMaxLimitExceeded] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{currentTotal: number, limit: number} | null>(null);
  console.log(quote, "kkkk");
  // Determine which credits to use based on quote type
  const currentCredits = useEmergencyCredits ? emergencyAvailableCredits : userCredits;
  const hasEnoughCredits = quote ? currentCredits >= quote.amount : false;
  const isLowCredit = !hasEnoughCredits && paymentMethod === "credits";

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccess(false);
      setIsProcessing(false);
      
      // Check if this is a return from credits page
      const quotePurchaseContext = localStorage.getItem("quotePurchaseContext");
      if (quotePurchaseContext) {
        try {
          const context = JSON.parse(quotePurchaseContext);
          // Check if context is recent (within 30 minutes) and matches current quote
          const isRecent = Date.now() - context.timestamp < 30 * 60 * 1000;
          if (isRecent && context.quoteId === quote?.id && context.paymentMethod === "credits") {
            console.log("🔄 Setting payment method to credits from return flow");
            setPaymentMethod("credits");
            // Clear the context since we've used it
            localStorage.removeItem("quotePurchaseContext");
          }
        } catch (error) {
          console.error("Error parsing quote purchase context:", error);
          localStorage.removeItem("quotePurchaseContext");
        }
      }
      
      // Check max purchase quote limit
      if (user?.id) {
        checkMaxPurchaseQuoteLimit(user.id, setting).then((result) => {
          if (result.exceeded) {
            setLimitInfo({ currentTotal: result.currentTotal, limit: result.limit });
            setMaxLimitExceeded(true);
          } else {
            setMaxLimitExceeded(false);
            setLimitInfo(null);
          }
        }).catch((error) => {
          console.error("Error checking max purchase quote limit:", error);
          setMaxLimitExceeded(false);
          setLimitInfo(null);
        });
      }
    }
  }, [isOpen, user?.id, setting, quote?.id]);

  // Early return if quote is null
  if (!quote) {
    return null;
  }

  const handlePromotionalPurchase = async () => {
    if (!quote) {
      setError(t("quotePurchaseModal.quoteInfoMissing"));
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(false);

    try {
      // Create quote with promotional (free) purchase type
      const quoteData = {
        amount: 0, // Free promotional quote
        purchaseType: { id: PurchaseQuoteTypeEnum.Promotional }, // ID: 4
        job: { id: quote.job?.id },
        user: { id: user?.id },
        status: { id: 1 } // Pending status
      };
      
      if (quote?.isEmergency) {
        quoteData["isEmergency"] = true;
      }
      
      const resultAction = await dispatch(createQuote(quoteData));

      if (createQuote.fulfilled.match(resultAction)) {
        setSuccess(true);
        // Keep processing state true while showing success message
        setTimeout(() => {
          setIsProcessing(false);
          onSuccess(resultAction.payload);
          onClose();
        }, 1500);
      } else {
        setIsProcessing(false);
        setError(resultAction.payload as string || t("quotePurchaseModal.claimFailedPromotion"));
      }

    } catch (err: any) {
      setIsProcessing(false);
      setError(err.message || t("quotePurchaseModal.claimFailedPromotion"));
    }
  };

  const handleCreditPayment = async () => {
    if (!quote) {
      setError(t("quotePurchaseModal.quoteInfoMissing"));
      return;
    }

    if (!hasEnoughCredits) {
      setError(t("quotePurchaseModal.insufficientCreditsError"));
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(false);

    try {
      // Create quote with credit payment
      const quoteData = {
        amount: quote.amount,
        purchaseType: { id: PurchaseQuoteTypeEnum.Credit },
        job: { id: quote.job?.id },
        user: { id: user?.id },
        status: { id: 1 } // Pending status
      };
      if (quote?.["isEmergency"]) {
        quoteData["isEmergency"] = true;
      }
      const resultAction = await dispatch(createQuote(quoteData));

      if (createQuote.fulfilled.match(resultAction)) {
        setSuccess(true);
        // Refetch credits after successful purchase
        if (refetchCredits) {
          refetchCredits();
        }
        // Keep processing state true while showing success message
        setTimeout(() => {
          setIsProcessing(false);
          onSuccess(resultAction.payload);
          onClose();
        }, 1500);
      } else {
        setIsProcessing(false);
        setError(resultAction.payload as string || t("quotePurchaseModal.purchaseFailedCredits"));
      }

    } catch (err: any) {
      setIsProcessing(false);
      setError(err.message || t("quotePurchaseModal.purchaseFailedCredits"));
    }
  };

  const handleDollarPayment = () => {
    setShowCreditPaymentModal(true);
  };

  const handleDollarPaymentSuccess = async (paymentData: any) => {
    if (!quote) {
      setError(t("quotePurchaseModal.quoteInfoMissing"));
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(false);

    try {
      // Create quote with escrow reference
      const quoteData = {
        amount: quote.amount,
        purchaseType: { id: PurchaseQuoteTypeEnum.Purchase },
        escrow: { id: paymentData.escrowId },
        job: { id: quote.job?.id },
        user: { id: user?.id },
        status: { id: 1 } // Pending status
      };

      const resultAction = await dispatch(createQuote(quoteData));

      if (createQuote.fulfilled.match(resultAction)) {
        setSuccess(true);
        // Refetch credits after successful purchase
        if (refetchCredits) {
          refetchCredits();
        }
        // Keep processing state true while showing success message
        setTimeout(() => {
          setIsProcessing(false);
          onSuccess(resultAction.payload);
          onClose();
        }, 1500);
      } else {
        setIsProcessing(false);
        setError(resultAction.payload as string || t("quotePurchaseModal.purchaseFailedDollars"));
      }

    } catch (err: any) {
      setIsProcessing(false);
      setError(err.message || t("quotePurchaseModal.purchaseFailedDollars"));
    }
  };

  const handlePurchaseCredits = () => {
    // Store quote purchase context for return navigation
    const quotePurchaseContext = {
      quoteId: quote.id,
      amount: quote.amount,
      jobId: quote.job?.id,
      jobTitle: quote.job?.title,
      professionalId: quote.professional?.id,
      professionalName: `${quote.professional?.firstName} ${quote.professional?.lastName}`,
      isEmergency: quote.isEmergency,
      useEmergencyCredits: quote.isEmergency,
      returnTo: "/professional",
      timestamp: Date.now(),
      paymentMethod: "credits" // Set to credits for return flow
    };
    localStorage.setItem("quotePurchaseContext", JSON.stringify(quotePurchaseContext));
    
    // Navigate to credits page
    if(quote.isEmergency) {
      localStorage.setItem("credits_selected_tab", "emergency");
    } else {
      localStorage.setItem("credits_selected_tab", "regular");
    }
    navigate("/credits");
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">{t("quotePurchaseModal.title")}</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="p-2"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Purchase Limit Exceeded Message */}
            {maxLimitExceeded && limitInfo && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  <div>
                    <h4 className="font-medium text-red-800">{t("quotePurchaseModal.purchaseLimitExceeded")}</h4>
                    <p className="text-sm text-red-600 mt-1">
                      {t("quotePurchaseModal.purchaseLimitExceededDesc", { 
                        currencySymbol: getDynamicCurrencySymbol(), 
                        limit: limitInfo.limit.toFixed(2),
                        currentTotal: limitInfo.currentTotal.toFixed(2)
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Promotional Quote Notice */}
            {quote?.isPromotional && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎁</span>
                  <div>
                    <h3 className="font-medium text-purple-900">{t("quotePurchaseModal.promotionalTitle")}</h3>
                    <p className="text-sm text-purple-700 mt-1">
                      {t("quotePurchaseModal.promotionalDesc")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Quote Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-2">{t("quotePurchaseModal.quoteDetails")}</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>{t("quotePurchaseModal.job")}:</span>
                  <span className="font-medium">{quote?.job?.title || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t("quotePurchaseModal.professional")}:</span>
                  <span className="font-medium">
                    {quote?.professional?.firstName} {quote?.professional?.lastName}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-semibold">
                  <span>{t("quotePurchaseModal.amount")}:</span>
                  <span className={quote?.isPromotional ? "text-purple-600" : "text-blue-600"}>
                    {quote?.isPromotional
                      ? t("quotePurchaseModal.freePromotion")
                      : useEmergencyCredits
                        ? `${quote?.amount || 0} ${t("quotePurchaseModal.emergencyCredits")}`
                        : `${getDynamicCurrencySymbol()}${quote?.amount || 0}`
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Method Selection - Hide for promotional quotes */}
            {!quote?.isPromotional && (
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-4">{t("quotePurchaseModal.paymentMethod")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    variant={paymentMethod === "dollars" ? "default" : "outline"}
                    onClick={() => setPaymentMethod("dollars")}
                    className="flex items-center justify-center gap-2 h-12"
                  >
                    <span className="hidden sm:inline">{t("quotePurchaseModal.payWithCurrency")}</span>
                    <span className="sm:hidden">{t("quotePurchaseModal.currency")}</span>
                  </Button>
                  <Button
                    variant={paymentMethod === "credits" ? "default" : "outline"}
                    onClick={() => setPaymentMethod("credits")}
                    className="flex items-center justify-center gap-2 h-12"
                  >
                    <CreditCard className="h-4 w-4" />
                    <div className="flex flex-col items-center">
                      <span className="hidden sm:inline">
                        {useEmergencyCredits ? t("quotePurchaseModal.payWithEmergencyCredits") : t("quotePurchaseModal.payWithCredits")}
                      </span>
                      <span className="sm:hidden">
                        {useEmergencyCredits ? t("quotePurchaseModal.emergencyCredits") : t("quotePurchaseModal.credits")}
                      </span>
                    </div>
                  </Button>
                </div>
              </div>
            )}

            {/* Emergency Credits Notice */}
            {useEmergencyCredits && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <div>
                    <h3 className="font-medium text-red-900">{t("quotePurchaseModal.emergencyQuote")}</h3>
                    <p className="text-sm text-red-700">
                      {t("quotePurchaseModal.emergencyQuoteDesc")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Credit Balance Info - Hide for promotional quotes */}
            {!quote?.isPromotional && paymentMethod === "credits" && (
              <div className="mb-6">
                <div className={`flex items-center justify-between p-3 rounded-lg ${useEmergencyCredits ? "bg-blue-50" : "bg-blue-50"
                  }`}>
                  <span className={`text-sm ${useEmergencyCredits ? "text-red-700" : "text-blue-700"
                    }`}>
                    {useEmergencyCredits ? t("quotePurchaseModal.yourEmergencyCredits") : t("quotePurchaseModal.yourCredits")}
                  </span>
                  <span className={`font-medium ${useEmergencyCredits ? "text-red-900" : "text-blue-900"
                    }`}>
                    {currentCredits}
                  </span>
                </div>

                {isLowCredit && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-red-700 font-medium">
                          {useEmergencyCredits ? t("quotePurchaseModal.insufficientEmergencyCredits") : t("quotePurchaseModal.insufficientCredits")}
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          {useEmergencyCredits 
                            ? t("quotePurchaseModal.insufficientEmergencyCreditsDesc", { count: (quote?.amount || 0) - currentCredits })
                            : t("quotePurchaseModal.insufficientCreditsDesc", { count: (quote?.amount || 0) - currentCredits })
                          }
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 mt-2">
                          <Button
                            size="sm"
                            onClick={handlePurchaseCredits}
                            className="text-xs flex-1 p-2"
                          >
                            <span className="hidden sm:inline">
                              {useEmergencyCredits ? t("quotePurchaseModal.purchaseEmergencyCredits") : t("quotePurchaseModal.purchaseCredits")}
                            </span>
                            <span className="sm:hidden">
                              {useEmergencyCredits ? t("quotePurchaseModal.buyEmergencyCredits") : t("quotePurchaseModal.buyCredits")}
                            </span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPaymentMethod("dollars")}
                            className="text-xs flex-1 p-2"
                          >
                            <span className="hidden sm:inline">{t("quotePurchaseModal.payWithCurrency")}</span>
                            <span className="sm:hidden">{t("quotePurchaseModal.payWith")}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <p className="text-sm text-green-700">{t("quotePurchaseModal.quotePurchaseSuccess")}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 h-12"
                disabled={isProcessing}
              >
                {t("quotePurchaseModal.cancel")}
              </Button>
              <Button
                onClick={quote?.isPromotional ? handlePromotionalPurchase : (paymentMethod === "dollars" ? handleDollarPayment : handleCreditPayment)}
                disabled={isProcessing || (!quote?.isPromotional && paymentMethod === "credits" && isLowCredit) || maxLimitExceeded}
                className={`flex-1 h-12 ${quote?.isPromotional ? "bg-purple-600 hover:bg-purple-700" : ""} ${isProcessing ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="font-medium">{quote?.isPromotional ? t("quotePurchaseModal.claimingFreeQuote") : t("quotePurchaseModal.processingPayment")}</span>
                  </div>
                ) : quote?.isPromotional ? (
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🎁</span>
                    <span className="text-sm font-medium">{t("quotePurchaseModal.claimFreeQuote")}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <span className="text-sm font-medium">
                      {paymentMethod === "dollars"
                        ? t("quotePurchaseModal.purchaseFor", { currencySymbol: getDynamicCurrencySymbol(), amount: quote?.amount || 0 })
                        : t("quotePurchaseModal.purchaseForCredits", { amount: quote?.amount || 0 })
                      }
                    </span>
                    {paymentMethod === "credits" && (
                      <span className="text-xs opacity-90">
                        {useEmergencyCredits ? t("quotePurchaseModal.emergencyCredits") : t("quotePurchaseModal.credits")}
                      </span>
                    )}
                  </div>
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Credit Payment Modal */}
        <CreditPaymentModal
          isOpen={showCreditPaymentModal}
          onClose={() => setShowCreditPaymentModal(false)}
          onSuccess={handleDollarPaymentSuccess}
          planAmount={quote.amount}
          planName={`Quote Purchase - ${quote.job?.title}`}
          currencySymbol={getDynamicCurrencySymbol()}
        />
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default QuotePurchaseModal;
